import { Box, IconButton, InputBase, Paper, useTheme } from '@mui/material'
import React from 'react'
import { Icons } from '../../utils/icons.jsx'

/**
 * @typedef {'small' | 'medium' | 'large'} MUIButtonSize
 * @typedef {'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'inherit' | 'default'} MUIColor
 */
/**
 * A Custom Searchbar.
 *
 * @param {Object} props
 * @param {Boolean} [props.disabled=false] - Whether the button is disabled or not
 * @param {MUIButtonSize} [props.size="small"] - Size of the button
 * @param {React.ReactNode} [props.icon] - Icon to be displayed in the button
 * @param {Function} [props.onKeyDown] - Function to be called when the button is clicked
 * @param {Function} [props.onKeyUp] - Function to be called when the button is clicked
 * @param {React.CSSProperties} [props.style] - Style object for the button
 * @param {String} [props.border] - Border style for the button
 * @param {String} [props.background] - Background color for the button
 * @param {String} [props.placeholder] - Placeholder text for the button
 * @param {String} [props.value] - Value of the button
 * @param {Function} [props.onChange] - Function to be called when the button is clicked
 * @param {String} [props.width="220px"] - Width of the button
 */

const CustomSearchBar = ( { fullWidth = false, buttonIcon, showButton = false, onButtonClick = () => { }, inputStyles = {}, disabled = false, size = 'small', icon = undefined, onKeyDown = () => { }, onKeyUp = () => { }, style = {}, border, background, placeholder = "Search...", value, onChange = () => { }, width = "220px", ...restProps } ) => {

    const { palette, layout, components, border: defaultBorder } = useTheme()
    const buttonStyles = components?.MuiButton?.styleOverrides
    const buttonRadius = buttonStyles?.root?.borderRadius

    return (
        <Paper
            component="form"
            elevation={0}
            sx={{
                borderRadius: typeof buttonRadius === 'number' ? `${buttonRadius}px` : buttonRadius ?? layout.borderRadius,
                boxSizing: 'border-box',
                border: border || defaultBorder[1],
                bgcolor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                width: fullWidth ? "100%" : width,
                ":focus-within": {
                    outline: `1px solid ${palette.primary.light}`,
                },
                ...style
            }}
        >
            <Box
                component="span"
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    marginLeft: "10px"
                }}>{icon && icon}</Box>
            <InputBase
                size={size || "medium"}
                disabled={disabled}
                value={value}
                onChange={onChange}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                sx={{ flex: 1, minWidth: 0, background: "none" }}
                slotProps={{ input: { sx: { padding: "4px 10px", ...inputStyles } } }}
                placeholder={placeholder || "Search Users By Acahrya ID..."}
                {...restProps}
            />
            {showButton && <IconButton size={size || "medium"} type="button" sx={{ p: '4px', flexShrink: 0 }} onClick={onButtonClick} aria-label="search">
                {buttonIcon || Icons.default.SearchIcon}
            </IconButton>}
        </Paper>
    )
}

export default CustomSearchBar
